import { DATA, LAYOUT, Presets } from '@wix/component-protocol/schema';
import type { ContainerLayout } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import type { CompRef, Link, ValueOf } from '@wix/document-services-types';
import { merge } from 'lodash';

import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../../errors';

import { IManifestChangeDetection } from '../../ManifestChangeDetection';
import { ManifestComponentExtensionType } from '../../stores';
import { CacheMethodResponse } from '../decorators';
import { MethodCacheStrategy } from '../MethodCacheStrategy';
import { AbstractManifestAPI } from '../AbstractManifestAPI';
import {
  VirtualElement,
  VirtualElementAPIType,
} from '../providers/VirtualElement';
import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { ManifestQueryAPI } from '../../handlers';
import type {
  BindingTargetDetails,
  FailedToAttachError,
  FailedToBindError,
  FailedToGetBindingError,
  FailedToListBindingsError,
  FailedToListProvidersError,
  FailedToSetBindingError,
  FailedToUnbindError,
  FailedToDetachError,
  FailedToFindBindingCandidatesError,
} from '@wix/editor-platform-feature-store';
import {
  Provider,
  ListProvidersOptions,
  UnbindAll,
  BindingProperties,
  BindingDetailsMap,
  ListBindingsOptions,
  BindingCandidate,
  findBindingCandidatesOptions,
} from '@wix/public-editor-platform-interfaces';
import { ManifestProviderQuery } from './ManifestProviderQuery';
import type { IOperationContext } from '../OperationContext';
import { ManifestQueryOperationTracker } from './ManifestQueryOperationTracker';
import { DependenciesProvider } from '../providers/DependenciesProvider';

import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

interface IStylesValue {
  cssProperties?: Record<string, string>;
  cssCustomProperties?: Record<string, string>;
}

export class ManifestQuery extends AbstractManifestAPI<
  ManifestQueryAPI,
  | VirtualElementAPIType.ManifestQuery
  | VirtualElementAPIType.ManifestQueryReadonly
> {
  private manifestProviderQuery: ManifestProviderQuery;
  private operationTracker: ManifestQueryOperationTracker;
  constructor(
    node: VirtualElement<
      | VirtualElementAPIType.ManifestQuery
      | VirtualElementAPIType.ManifestQueryReadonly
    >,
    api: ManifestQueryAPI,
    editorPointerAPI: EditorPointerServiceAPI,
    manifestChangeDetection: IManifestChangeDetection,
    manifestServiceFlags: ManifestServiceFlags,
    manifestServiceStats: ManifestServiceStats,
    dependenciesProvider: DependenciesProvider,
    protected isReadonly = false,
    operationContext?: IOperationContext,
  ) {
    super(
      node,
      api,
      editorPointerAPI,
      manifestChangeDetection,
      manifestServiceFlags,
      manifestServiceStats,
    );
    this.manifestProviderQuery = new ManifestProviderQuery(
      node,
      api,
      editorPointerAPI,
      manifestChangeDetection,
      manifestServiceFlags,
      manifestServiceStats,
    );

    this.operationTracker = new ManifestQueryOperationTracker(
      node,
      dependenciesProvider,
      api,
    );
  }

  private ensureIfWritable() {
    if (this.isReadonly) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ManifestQuery,
        'query instance is readonly',
      );
    }
  }

  @CacheMethodResponse({
    namespace: 'ManifestQuery',
    cacheStrategy: MethodCacheStrategy.Permanent,
    transformResponseForStatsCollecting: (x: ManifestQuery[]) =>
      x.map((child) => child.getOwnProperties()),
  })
  getChildren() {
    return this.node.getChildren().map((child) => {
      return child.getAPI();
    });
  }

  @CacheMethodResponse({
    namespace: 'ManifestQuery',
    cacheStrategy: MethodCacheStrategy.DataUpdate,
  })
  getData(): Record<string, any> {
    const editorPointers =
      this.virtualManifestOperations.getEditorPointers('data');

    const contexts = editorPointers.map(({ editorPointer, extensionType }) => {
      return {
        editorPointer,
        extensionType,
        data: this.api.data.getData(editorPointer, extensionType, {
          customGetDefaultData: () => {
            const api = this.node.getAPI(
              VirtualElementAPIType.ManifestResolver,
            );
            return api.getDefaultDataValues();
          },
        }),
      };
    });

    const values: Record<string, any>[] = [];

    contexts.forEach(({ editorPointer, extensionType, data }) => {
      const mappings = this.virtualManifestOperations.getMappings(
        editorPointer,
        'data',
      );

      const _values = this.virtualManifestOperations.decodeValues(
        data,
        mappings,
      );

      const props = this.api.props.getProps(editorPointer, extensionType);

      Object.entries(_values).forEach(([key, value]) => {
        const dataType =
          this.virtualManifest.tree.manifest?.editorElement?.data?.[key]
            ?.dataType;

        if (
          dataType &&
          [
            DATA.DATA_TYPE.link,
            DATA.DATA_TYPE.vectorArt,
            DATA.DATA_TYPE.menuItems,
          ].includes(dataType)
        ) {
          if (
            (typeof value === 'string' || value === undefined) &&
            dataType === DATA.DATA_TYPE.menuItems
          ) {
            _values[key] = props[key] ?? [];
          } else if (_values[key] || props[key]) {
            _values[key] = merge(_values[key], props[key] ?? {});
          }
        }

        if (value && dataType === DATA.DATA_TYPE.link) {
          _values[key] = merge(_values[key], {
            displayName: this.api.resolveLinkDisplayName(value as Link),
          });
        }
      });

      values.push(_values);
    });

    return this.virtualManifestOperations.mergeValues(values);
  }

  setData(values: Record<string, unknown>) {
    this.ensureIfWritable();

    const editorPointers =
      this.virtualManifestOperations.getEditorPointers('data');

    const contexts: {
      editorPointer: EditorPointer<CompRef>;
      extensionType: ManifestComponentExtensionType;
      data: Record<string, unknown>;
    }[] = [];

    editorPointers.forEach((context) => {
      const mappings = this.virtualManifestOperations.getMappings(
        context.editorPointer,
        'data',
      );

      const encodedData = this.virtualManifestOperations.encodeValues(
        values,
        mappings,
      );

      if (Object.keys(encodedData).length) {
        contexts.push({
          editorPointer: context.editorPointer,
          extensionType: context.extensionType,
          data: encodedData,
        });
      }
    });

    if (this.manifestServiceFlags.debug) {
      console.groupCollapsed('ManifestQuery.setData');
      console.log('– values', values);
      console.log('– mappings', this.virtualManifest.tree.mappings.data);
      console.log('– values to external API', contexts);
      console.groupEnd();
    }

    contexts.forEach(({ editorPointer, extensionType, data }) => {
      return this.api.data.setData(editorPointer, extensionType, data);
    });
  }

  @CacheMethodResponse({
    namespace: 'ManifestQuery',
    cacheStrategy: MethodCacheStrategy.StylesUpdate,
    // bypassTransaction: true
  })
  getStyles(): {
    cssProperties: Record<string, string>;
    cssCustomProperties: Record<string, string>;
  } {
    const editorPointers = [
      ...this.virtualManifestOperations.getEditorPointers(
        'cssProperties',
        'cssCustomProperties',
      ),
    ];

    const contexts = editorPointers.map(({ editorPointer, extensionType }) => {
      return {
        editorPointer,
        styles: this.api.styles.getStyles(editorPointer, extensionType, {
          customGetDefaultStyle: () => {
            const api = this.node.getAPI(
              VirtualElementAPIType.ManifestResolver,
            );

            return api.getDefaultStyleValues();
          },
        }),
      };
    });

    const styles: {
      cssProperties: Record<string, string>[];
      cssCustomProperties: Record<string, string>[];
    } = {
      cssProperties: [],
      cssCustomProperties: [],
    };

    contexts.forEach((context) => {
      const cssPropertiesMappings = this.virtualManifestOperations.getMappings(
        context.editorPointer,
        'cssProperties',
        { shouldExpandCssProperties: true },
      );

      const cssCustomPropertiesMappings =
        this.virtualManifestOperations.getMappings(
          context.editorPointer,
          'cssCustomProperties',
        );

      const cssProperties = this.virtualManifestOperations.decodeValues(
        context.styles?.cssProperties ?? {},
        cssPropertiesMappings,
      );

      const cssCustomProperties = this.virtualManifestOperations.decodeValues(
        context.styles?.cssCustomProperties ?? {},
        cssCustomPropertiesMappings,
      );

      styles.cssProperties.push(cssProperties);
      styles.cssCustomProperties.push(cssCustomProperties);
    });

    return {
      cssProperties: this.virtualManifestOperations.mergeValues(
        styles.cssProperties,
      ),
      cssCustomProperties: this.virtualManifestOperations.mergeValues(
        styles.cssCustomProperties,
      ),
    };
  }

  setStyles(values: IStylesValue) {
    this.ensureIfWritable();

    const editorPointers = this.virtualManifestOperations.getEditorPointers(
      'cssProperties',
      'cssCustomProperties',
    );

    const contexts: {
      editorPointer: EditorPointer<CompRef>;
      extensionType: ManifestComponentExtensionType;
      styles: {
        cssProperties: Record<string, string>;
        cssCustomProperties: Record<string, string>;
      };
    }[] = [];

    editorPointers.forEach((context) => {
      const cssPropertiesMappings = this.virtualManifestOperations.getMappings(
        context.editorPointer,
        'cssProperties',
        {
          shouldExpandCssProperties: true,
        },
      );

      const cssCustomPropertiesMappings =
        this.virtualManifestOperations.getMappings(
          context.editorPointer,
          'cssCustomProperties',
        );

      const cssProperties = this.virtualManifestOperations.encodeValues(
        values.cssProperties ?? {},
        cssPropertiesMappings,
      );

      const cssCustomProperties = this.virtualManifestOperations.encodeValues(
        values.cssCustomProperties ?? {},
        cssCustomPropertiesMappings,
      );

      if (
        Object.keys(cssProperties).length ||
        Object.keys(cssCustomProperties).length
      ) {
        contexts.push({
          editorPointer: context.editorPointer,
          extensionType: context.extensionType,
          styles: {
            cssProperties,
            cssCustomProperties,
          },
        });
      }
    });

    if (this.manifestServiceFlags.debug) {
      console.groupCollapsed('ManifestQuery.setStyles');
      console.log('– values', values);
      console.log('– mappings', {
        cssProperties: this.virtualManifest.tree.mappings.cssProperties,
        cssCustomProperties:
          this.virtualManifest.tree.mappings.cssCustomProperties,
      });
      console.log('– values to external API', contexts);
      console.groupEnd();
    }

    contexts.forEach(({ editorPointer, extensionType, styles }) => {
      return this.operationTracker.setStyles(
        editorPointer,
        extensionType,
        styles,
      );
    });
  }

  removeStyles(values: {
    cssPropertiesKeys?: string[];
    cssCustomPropertiesKeys?: string[];
  }) {
    this.ensureIfWritable();

    const editorPointers = this.virtualManifestOperations.getEditorPointers(
      'cssProperties',
      'cssCustomProperties',
    );

    const contexts: {
      editorPointer: EditorPointer<CompRef>;
      extensionType: ManifestComponentExtensionType;
      styles: {
        cssPropertiesKeys: string[];
        cssCustomPropertiesKeys: string[];
      };
    }[] = [];

    editorPointers.forEach((context) => {
      const cssPropertiesMappings = this.virtualManifestOperations.getMappings(
        context.editorPointer,
        'cssProperties',
      );

      const cssCustomPropertiesMappings =
        this.virtualManifestOperations.getMappings(
          context.editorPointer,
          'cssCustomProperties',
        );

      const cssPropertiesKeys = this.virtualManifestOperations.getOriginalKeys(
        values.cssPropertiesKeys ?? [],
        cssPropertiesMappings,
      );

      const cssCustomPropertiesKeys =
        this.virtualManifestOperations.getOriginalKeys(
          values.cssCustomPropertiesKeys ?? [],
          cssCustomPropertiesMappings,
        );

      if (cssPropertiesKeys.length || cssCustomPropertiesKeys.length) {
        contexts.push({
          editorPointer: context.editorPointer,
          extensionType: context.extensionType,
          styles: {
            cssPropertiesKeys,
            cssCustomPropertiesKeys,
          },
        });
      }
    });

    if (this.manifestServiceFlags.debug) {
      console.groupCollapsed('ManifestQuery.removeStyles');
      console.log('– values', values);
      console.log('– mappings', {
        cssProperties: this.virtualManifest.tree.mappings.cssProperties,
        cssCustomProperties:
          this.virtualManifest.tree.mappings.cssCustomProperties,
      });
      console.log('– values to external API', contexts);
      console.groupEnd();
    }

    contexts.forEach(({ editorPointer, extensionType, styles }) => {
      return this.api.styles.removeStyles(editorPointer, extensionType, styles);
    });
  }

  /**
   * @deprecated use getData() instead
   */
  getResolvedData({ dataItemKey }: { dataItemKey: string }) {
    const definition =
      this.virtualManifest.tree.manifest.editorElement?.data?.[dataItemKey];

    if (!definition) {
      return;
    }

    switch (definition.dataType) {
      case DATA.DATA_TYPE.vectorArt: {
        const allData = this.getData();
        const vectorArtData = allData[dataItemKey];

        return this.api.resolvedData.getVectorArtResolvedData(vectorArtData);
      }

      default: {
        console.warn(
          createEditorPlatformManifestServicesError(
            EditorPlatformManifestServicesErrorCode.ManifestQuery,
            `"${definition.dataType}" is not supported yet for resolving`,
          ),
        );
        return;
      }
    }
  }

  /**
   * NOTE: we need to get preset from all components and
   * - return its values are the same
   * - return MIX if values are different
   */
  @CacheMethodResponse({
    namespace: 'ManifestQuery',
    cacheStrategy: MethodCacheStrategy.PresetUpdate,
  })
  getAppliedPreset() {
    if (this.node.isMultipleComponentsRoot()) {
      return null;
    }

    const editorPointer = this.node.getEditorPointer();
    const extensionType = this.node.getExtensionType();

    return (
      this.api.presets.getAppliedPreset(editorPointer, extensionType) ?? null
    );
  }

  private applyInnerElementsStyleOverrides(
    elements: NonNullable<
      NonNullable<
        ValueOf<NonNullable<ValueOf<Presets>['styleOverrides']>>
      >['elements']
    >,
  ) {
    Object.entries(elements).forEach(([elementKey, elementStyleOverrides]) => {
      const childManifestQuery = this.node.getChild([elementKey])?.getAPI();

      if (!childManifestQuery) {
        console.error(
          createEditorPlatformManifestServicesError(
            EditorPlatformManifestServicesErrorCode.ManifestQuery,
            `missing child ${elementKey}`,
          ),
        );
        return;
      }

      const {
        elements: innerElements,
        presetKey,
        cssProperties,
        cssCustomProperties,
      } = elementStyleOverrides;

      if (presetKey) {
        childManifestQuery.applyPreset({
          key: presetKey,
        });
      }

      childManifestQuery.applyBreakpointStyleOverrides({
        cssProperties,
        cssCustomProperties,
        elements: innerElements,
      });
    });
  }

  applyBreakpointStyleOverrides(
    styleOverrides: NonNullable<
      ValueOf<NonNullable<ValueOf<Presets>['styleOverrides']>>
    >,
  ) {
    const { cssProperties, cssCustomProperties, elements } = styleOverrides;

    const mappedCssPropertiesOverrides = Object.fromEntries(
      Object.entries(cssProperties ?? {}).map(([key, { value }]) => [
        key,
        value,
      ]),
    );

    const mappedCssCustomPropertiesOverrides = Object.fromEntries(
      Object.entries(cssCustomProperties ?? {}).map(([key, { value }]) => [
        key,
        value,
      ]),
    );

    if (
      Object.keys(mappedCssPropertiesOverrides).length ||
      Object.keys(mappedCssCustomPropertiesOverrides).length
    ) {
      this.setStyles({
        cssProperties: mappedCssPropertiesOverrides,
        cssCustomProperties: mappedCssCustomPropertiesOverrides,
      });
    }

    // TODO: [EP-6733] How to apply state overrides?
    if (elements) {
      this.applyInnerElementsStyleOverrides(elements);
    }
  }

  private mapBreakpointPresetStyleOverridesKeyToBreakpoint(
    key: Omit<
      keyof NonNullable<NonNullable<ValueOf<Presets>['styleOverrides']>>,
      'default'
    >,
  ): ValueOf<typeof LAYOUT.BREAKPOINT> {
    switch (key) {
      case 'small':
        return LAYOUT.BREAKPOINT.small;
      default:
        console.error(
          `Unknown breakpoint override key ${key}, only supported keys: ${typeof key}`,
        );
        return LAYOUT.BREAKPOINT.UNKNOWN_Breakpoint;
    }
  }

  applyPreset({ key: presetKey }: { key: string }) {
    const mappings =
      this.virtualManifest.tree.mappings.presets?.[presetKey] ?? [];

    const presetDefinition =
      this.virtualManifest.tree.manifest.editorElement?.presets?.[presetKey];

    if (!mappings.length || !presetDefinition) {
      return;
    }

    const activePresetKey = this.getAppliedPreset();
    const activePresetDefinition = activePresetKey
      ? this.virtualManifest.tree.manifest.editorElement?.presets?.[
          activePresetKey
        ]
      : undefined;

    mappings.forEach((config) => {
      // TODO: Make sure all actions are done in a single transaction - to mobile as well (to make history undo/redo make all changes at once)
      const { default: defaultStyleOverrides, ...breakpointStyles } =
        presetDefinition.styleOverrides ?? {};

      if (defaultStyleOverrides) {
        this.node
          // TODO: what is this pointer? perhaps withBreakpointVariant won't do what we expect
          // .withBreakpointVariant(config.editorPointer)
          .getAPI()
          .applyBreakpointStyleOverrides(defaultStyleOverrides);
      }

      Object.entries(breakpointStyles).forEach(
        ([breakpoint, styleOverrides]) => {
          const editorPointer = this.api.stage.getPointerWithBreakpointVariant(
            config.editorPointer,
            this.mapBreakpointPresetStyleOverridesKeyToBreakpoint(breakpoint),
          );
          if (editorPointer) {
            const breakpointAppliedPreset =
              this.api.presets.getScopedAppliedPreset(
                editorPointer,
                config.extensionType,
              );
            if (
              !breakpointAppliedPreset ||
              this.editorPointerAPI.isSameEditorPointer(
                editorPointer,
                config.editorPointer,
              )
            ) {
              this.node
                .withBreakpointVariant(editorPointer)
                .getAPI()
                .applyBreakpointStyleOverrides(styleOverrides);
            }
          }
        },
      );

      const targetSize = presetDefinition.initialSize;

      if (targetSize && this.node.isRoot()) {
        this.operationTracker.applyComponentResize(
          config.editorPointer,
          targetSize,
        );
      }

      if (
        this.manifestServiceFlags.isSpecEnabled('dmSystemContainerMinWidth')
      ) {
        this.regenerateContainersOnPresetChange(
          config.editorPointer,
          presetDefinition.presetDefaults?.containers,
          activePresetDefinition?.presetDefaults?.containers,
        );
        // TODO: [EP-8823] https://wix.atlassian.net/browse/EP-8823 recurse into inner element containers, mirroring `applyInnerElementsStyleOverrides`.
      }

      // IMPORTANT: setting the preset last to make sure it doesn't cascade to other breakpoints for OOIs
      return this.operationTracker.applyPreset(
        config.editorPointer,
        config.extensionType,
        presetKey,
      );
    });
  }

  private regenerateContainersOnPresetChange(
    editorPointer: EditorPointer<CompRef>,
    containers: Record<string, ContainerLayout> | undefined,
    activeContainers: Record<string, ContainerLayout> | undefined,
  ) {
    if (!containers) {
      return;
    }

    const containerInfoMap =
      this.api.slots.getPopulatedContainerInfoMap(editorPointer);

    if (!containerInfoMap) {
      return;
    }

    Object.entries(containers).forEach(([containerKey, containerLayout]) => {
      const containerInfo = Object.values(containerInfoMap).find(
        (info) => info.slotName === containerKey,
      );

      if (!containerInfo) {
        return;
      }

      const containerPointer = this.editorPointerAPI.buildEditorPointer(
        containerInfo.compRef,
      );

      const activeContainerLayout = activeContainers?.[containerKey];

      this.operationTracker.regenerateContainerLayout(containerPointer, {
        containerLayout,
        activeContainerLayout,
      });
    });
  }

  /**
   * NOTE: we need to get state from all components and
   * - return its values are the same
   * - return MIX if values are different
   */
  @CacheMethodResponse({
    namespace: 'ManifestQuery',
    cacheStrategy: MethodCacheStrategy.StateUpdate,
  })
  getState() {
    if (this.node.isMultipleComponentsRoot()) {
      return null;
    }

    const editorPointer = this.node.getEditorPointer();
    const extensionType = this.node.getExtensionType();

    const hasBuilderStates =
      Object.keys(
        this.virtualManifest.tree.manifest.editorElement?.states || {},
      ).length > 0;

    if (!hasBuilderStates) {
      return this.api.state.getViewState(editorPointer, extensionType);
    }

    return this.api.state.getCurrentState(editorPointer, extensionType);
  }

  resetStates() {
    const editorPointer = this.node.getEditorPointer();
    const extensionType = this.node.getExtensionType();

    const hasBuilderStates =
      Object.keys(
        this.virtualManifest.tree.manifest.editorElement?.states || {},
      ).length > 0;

    if (!hasBuilderStates) {
      return this.api.state.resetViewStates(editorPointer, extensionType);
    }

    this.api.props.removePropsOverride(editorPointer, extensionType);
    this.api.state.resetCurrentStates(editorPointer, extensionType);
  }

  getDesignState(): string[] {
    const editorPointers = this.node.getEditorPointers();
    return this.api.state.getDesignStates(editorPointers);
  }

  setDesignState(states: string[]): void {
    const editorPointers = this.node.getEditorPointers();
    this.api.state.setDesignStates(editorPointers, states);
  }

  resetDesignState(): void {
    const editorPointers = this.node.getEditorPointers();
    this.api.state.resetDesignStates(editorPointers);
  }

  setState(state: string) {
    const hasBuilderStates =
      Object.keys(
        this.virtualManifest.tree.manifest.editorElement?.states || {},
      ).length > 0;

    const mappings = this.virtualManifest.tree.mappings.states?.[state];
    const stateDefinition =
      this.virtualManifest.tree.manifest.editorElement?.states?.[state];

    if (!hasBuilderStates) {
      // check if this.node.isMultipleComponentsRoot()
      const editorPointer = this.node.getEditorPointer();
      const extensionType = this.node.getExtensionType();

      this.api.state.setViewState(editorPointer, extensionType, state);
      return;
    }

    if (!mappings?.length || !stateDefinition) {
      return;
    }

    mappings.forEach((context) => {
      this.api.props.setPropsOverride(
        context.editorPointer,
        context.extensionType,
        stateDefinition.props || {},
      );

      this.api.state.setCurrentState(
        context.editorPointer,
        context.extensionType,
        state,
      );
    });
  }

  listProviders(
    options?: ListProvidersOptions,
  ): FailedToListProvidersError | Provider[] {
    return this.manifestProviderQuery.listProviders(options);
  }

  async attachProvider(
    providerIdentifier: string,
    data?: Object,
  ): Promise<FailedToAttachError | void> {
    return this.manifestProviderQuery.attachProvider(providerIdentifier, data);
  }

  async detachProvider(
    providerIdentifier: string,
  ): Promise<FailedToDetachError | void> {
    return this.manifestProviderQuery.detachProvider(providerIdentifier);
  }

  async bind(bindings: BindingProperties[]): Promise<void | FailedToBindError> {
    return this.manifestProviderQuery.bind(bindings);
  }

  async setBinding(
    bindings: BindingDetailsMap,
  ): Promise<void | FailedToSetBindingError> {
    return this.manifestProviderQuery.setBinding(bindings);
  }

  async unbind(
    props: string[] | UnbindAll,
  ): Promise<void | FailedToUnbindError> {
    return this.manifestProviderQuery.unbind(props);
  }

  listBindings(
    options?: ListBindingsOptions,
  ): FailedToListBindingsError | BindingProperties[] {
    return this.manifestProviderQuery.listBindings(options);
  }

  getBinding(
    options?: ListBindingsOptions,
  ): FailedToGetBindingError | BindingDetailsMap {
    return this.manifestProviderQuery.getBinding(options);
  }

  findBindingCandidates(
    bindingTargetDetails: BindingTargetDetails,
    options?: findBindingCandidatesOptions,
  ): BindingCandidate[] | FailedToFindBindingCandidatesError {
    return this.manifestProviderQuery.findBindingCandidates(
      bindingTargetDetails,
      options,
    );
  }
}
